/**
 * Sección "Justificaciones" de Asistencia (008 · T111; contracts/ui.md §2.2, spec US7-2, US7-3,
 * US7-6 y FR-026).
 *
 * Card con `h2` arriba de los días, solo si hay algo que mostrar (como "Regularizaciones" de la
 * 007). Una fila por justificación, en el orden del servidor, con el tipo y el día, el estado
 * (badge icono + texto) y su detalle:
 *
 * | Estado | Detalle | Acción |
 * |---|---|---|
 * | `PENDING` | tu motivo (y "Ya no aplica" si `still_applies = false`) | "Cancelar solicitud" si `can_cancel` |
 * | `APPROVED` | tu motivo + nota del administrador | — |
 * | `REJECTED` | "Motivo: …" (el del rechazo) | — |
 * | `CANCELLED` | la causa | — |
 *
 * Nunca se muestran nombres de administradores (FR-026): una registrada por el Admin dice "La
 * registró tu administrador". Con la empresa suspendida, mismos estados sin acciones (FR-037); sin
 * red, cancelar queda deshabilitado. Cancelar no es optimista: se espera la respuesta; un `409
 * JUSTIFICATION_NOT_OPEN` cierra la hoja y avisa.
 */
import { useState } from 'react';

import type { StaffJustification } from '@fermin/contracts';

import { ApiError } from '../../api/client';
import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/cn';
import { t } from '../../i18n';

import { useCancelJustification } from './api';
import { JustificationCancelSheet } from './cancel-sheet';
import { itemTitle, kindWithMinutes, STATUS_ICON, STATUS_TONE, statusLabel } from './labels';

export const JUSTIFICATIONS_SECTION_ID = 'justificaciones';

/** Líneas de detalle de la fila según el estado (textos es-PE). */
export function detailLines(item: StaffJustification): string[] {
  const lines: string[] = [];
  if (item.kind !== 'ABSENCE' && item.observed_minutes !== null) {
    lines.push(kindWithMinutes(item.kind, item.observed_minutes));
  }
  if (item.origin === 'ADMIN') {
    lines.push(t('justification.section.byAdmin'));
  } else {
    lines.push(t('justification.section.yourReason', { reason: item.reason }));
  }
  switch (item.status) {
    case 'PENDING':
      if (!item.still_applies) lines.push(t('justification.section.noLongerApplies'));
      break;
    case 'APPROVED':
      if (item.resolution_note) {
        lines.push(t('justification.section.adminNote', { note: item.resolution_note }));
      }
      break;
    case 'REJECTED':
      if (item.resolution_note) {
        lines.push(t('justification.section.rejectedReason', { reason: item.resolution_note }));
      }
      break;
    case 'CANCELLED':
      if (item.cancel_reason) lines.push(t(`justification.cancelReasons.${item.cancel_reason}`));
      break;
  }
  return lines;
}

export function JustificationStatusBadge({ item }: { item: StaffJustification }) {
  const Icon = STATUS_ICON[item.status];
  return (
    <span
      data-testid="justification-badge"
      data-status={item.status}
      className={cn(
        'inline-flex w-fit items-center gap-fm-1 rounded-fm-pill px-fm-2 py-0.5 font-ui text-fm-sm font-medium',
        STATUS_TONE[item.status],
      )}
    >
      <Icon aria-hidden="true" size={14} />
      {statusLabel(item.status)}
    </span>
  );
}

interface RowProps {
  item: StaffJustification;
  readOnly: boolean;
  online: boolean;
  onCancel: (item: StaffJustification) => void;
}

function JustificationRow({ item, readOnly, online, onCancel }: RowProps) {
  const canCancel = !readOnly && item.status === 'PENDING' && item.can_cancel;
  return (
    <li
      data-testid="justification-item"
      data-status={item.status}
      data-day={item.day}
      data-kind={item.kind}
      className="flex flex-col gap-fm-2 py-fm-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-fm-2">
        <span className="font-ui text-fm-base font-semibold text-fm-text">
          {itemTitle(item.kind, item.day)}
        </span>
        <JustificationStatusBadge item={item} />
      </div>
      {detailLines(item).map((line) => (
        <p
          key={line}
          data-testid="justification-detail"
          className="break-words text-fm-sm text-fm-text-secondary"
        >
          {line}
        </p>
      ))}
      {canCancel ? (
        <Button
          data-testid="justification-cancel"
          variant="ghost"
          size="md"
          fullWidth={false}
          className="self-start"
          disabled={!online}
          onClick={() => onCancel(item)}
        >
          {t('justification.section.cancel')}
        </Button>
      ) : null}
    </li>
  );
}

export interface JustificationsSectionProps {
  items: readonly StaffJustification[];
  /** Empresa suspendida: mismos estados, sin acciones (FR-037). */
  readOnly: boolean;
  online: boolean;
}

export function JustificationsSection({ items, readOnly, online }: JustificationsSectionProps) {
  const cancel = useCancelJustification();
  const [target, setTarget] = useState<StaffJustification | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (items.length === 0) {
    return null;
  }

  const closeSheet = () => {
    if (!cancel.isPending) {
      setTarget(null);
      setCancelError(null);
    }
  };

  const confirmCancel = () => {
    if (target === null) return;
    setCancelError(null);
    cancel.mutate(target.id, {
      onSuccess: () => {
        setTarget(null);
        setNotice(null);
      },
      onError: (error) => {
        if (
          error instanceof ApiError &&
          (error.is('JUSTIFICATION_NOT_OPEN') || error.is('NOT_FOUND'))
        ) {
          setTarget(null);
          setNotice(t('justification.section.notOpen'));
          return;
        }
        setCancelError(t('justification.cancelSheet.error'));
      },
    });
  };

  return (
    <section
      id={JUSTIFICATIONS_SECTION_ID}
      data-testid="justifications-section"
      aria-labelledby="justifications-section-title"
      className="flex scroll-mt-fm-4 flex-col gap-fm-2 rounded-fm-lg border border-fm-border bg-fm-surface p-fm-4 shadow-fm-1"
    >
      <h2
        id="justifications-section-title"
        className="font-display text-fm-lg font-bold text-fm-text"
      >
        {t('justification.section.title')}
      </h2>
      {readOnly ? (
        <p data-testid="justifications-read-only" className="text-fm-sm text-fm-text-secondary">
          {t('justification.section.readOnly')}
        </p>
      ) : null}
      {notice !== null ? (
        <p data-testid="justifications-notice" role="status" className="text-fm-base text-fm-text">
          {notice}
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-fm-border">
        {items.map((item) => (
          <JustificationRow
            key={item.id}
            item={item}
            readOnly={readOnly}
            online={online}
            onCancel={(selected) => {
              setCancelError(null);
              setTarget(selected);
            }}
          />
        ))}
      </ul>
      <JustificationCancelSheet
        open={target !== null}
        pending={cancel.isPending}
        error={cancelError}
        onConfirm={confirmCancel}
        onClose={closeSheet}
      />
    </section>
  );
}
